import { InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Badge,
  DataList,
  Flex,
  Heading,
  Separator,
  Text,
} from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import { HPSummarySectionCard } from 'components/sections/hitter-library/profile/summary/sections/section-card';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { MachineContext } from 'contexts/machine.context';
import { getYear } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { HitterProfileHelper } from 'lib_ts/classes/hitter-profile.helper';
import { meanObject } from 'lib_ts/classes/math.utilities';
import { SessionHelper } from 'lib_ts/classes/session.helper';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { HitterProfilePeriod } from 'lib_ts/enums/hitters.enums';
import { MAX_HIT_DISTANCE_FT } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import { isNil, max } from 'lodash';
import { useContext, useMemo } from 'react';

interface IPeriodSummary extends Partial<ISHHitDetails> {
  maxExitMPH: Nullable<number>;
  maxDistanceFT: Nullable<number>;
}

const formatDate = (start: Date, end: Date) => {
  const formatWithYear = 'MMM d, yyyy';
  const formatWithoutYear = 'MMM d';

  const startYear = getYear(start);
  const endYear = getYear(end);
  const sameYear = startYear === endYear;

  // Dec 15, 2024 - Jan 15, 2025 vs. Nov 15 - Dec 15, 2024
  const startFormat = sameYear ? formatWithoutYear : formatWithYear;
  const endFormat = formatWithYear;

  const startStr = format(start, startFormat, { timeZone: LOCAL_TIMEZONE });
  const endStr = format(end, endFormat, { timeZone: LOCAL_TIMEZONE });

  return `${startStr} - ${endStr}`;
};

const getTooltipDates = (period: HitterProfilePeriod) => {
  const prevStart = HitterProfileHelper.getPreviousStartDate(period);
  const prevEnd = HitterProfileHelper.getPreviousEndDate(period);

  const currStart = HitterProfileHelper.getCurrentStartDate(period);
  const currEnd = new Date();

  const prevRange = formatDate(prevStart, prevEnd);
  const currRange = formatDate(currStart, currEnd);

  return (
    <Flex direction="column" gap="1">
      <Text>Current period: {currRange}</Text>
      <Text>Previous period: {prevRange}</Text>
    </Flex>
  );
};

const DeltaBadge = (props: { delta: number }) => {
  const { delta } = props;

  if (!delta) {
    return null;
  }

  // skip if we'd show a 0 delta after rounding
  const noChange = Math.abs(delta).toFixed(0) === '0';
  if (noChange) {
    return null;
  }

  switch (Math.sign(delta)) {
    case 1: {
      return <Badge color={RADIX.COLOR.SUCCESS}>+{delta.toFixed(0)}%</Badge>;
    }

    case -1: {
      return (
        <Badge color={RADIX.COLOR.WARNING}>
          -{Math.abs(delta).toFixed(0)}%
        </Badge>
      );
    }

    case 0:
    default: {
      return null;
    }
  }
};

const DataRow = (props: {
  label: string;
  value: string;
  delta: Nullable<number> | undefined;
}) => {
  return (
    <DataList.Item align="stretch">
      <DataList.Label>
        <Text truncate size="3">
          {props.label}
        </Text>
      </DataList.Label>
      <DataList.Value style={{ marginLeft: 'auto' }}>
        <Flex align="center" gap="2">
          <Text size="3">{props.value}</Text>
          {!isNil(props.delta) && <DeltaBadge delta={props.delta} />}
        </Flex>
      </DataList.Value>
    </DataList.Item>
  );
};

const formatHitStat = (
  value: Nullable<number> | undefined,
  roundTo = 1,
  unit: string,
  toMetricFn?: (val: number) => number
) => {
  if (isNil(value)) {
    return '-';
  }

  return (
    SessionHelper.formatHitStat(value, {
      roundTo,
      toMetricFn,
    }) + unit
  );
};

const getMetricDelta = (
  currAvg: Nullable<number> | undefined,
  prevAvg: Nullable<number> | undefined
) => {
  if (!currAvg || !prevAvg) {
    return null;
  }

  return currAvg - prevAvg;
};

interface IKeyMetrics {
  current: IPeriodSummary;
  previous: IPeriodSummary;
  deltas: IPeriodSummary;
}

export const HPSummaryKeyMetricAverages = () => {
  const { machine } = useContext(MachineContext);
  const currSummaryHits = useHitterProfileStore((s) => s.currSummaryHits);
  const prevSummaryHits = useHitterProfileStore((s) => s.prevSummaryHits);
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  // hit counts per date
  const data = useMemo((): IKeyMetrics => {
    // remove hits we've deemed invalid or the user has excluded from averages
    const validCurrHits = currSummaryHits
      .filter((h) => !h.hit?.invalid)
      .filter(
        // handling for legacy data that hasn't been marked invalid but has extreme distance
        (h) => h.hit?.distanceFT && h.hit?.distanceFT <= MAX_HIT_DISTANCE_FT
      )
      .filter((h) => !h.avg_excluded);

    const validPrevHits = prevSummaryHits
      .filter((h) => !h.hit?.invalid)
      .filter(
        // handling for legacy data that hasn't been marked invalid but has extreme distance
        (h) => h.hit?.distanceFT && h.hit?.distanceFT <= MAX_HIT_DISTANCE_FT
      )
      .filter((h) => !h.avg_excluded);

    const [currAvg, prevAvg] = [validCurrHits, validPrevHits].map((hits) => {
      const details: Partial<ISHHitDetails>[] = [];

      hits.forEach((g) => {
        if (!g.hit) {
          return;
        }

        details.push(g.hit);
      });

      return meanObject(details);
    });

    const output: IKeyMetrics = {
      current: {
        exitMPH: currAvg?.exitMPH ?? null,
        distanceFT: currAvg?.distanceFT ?? null,
        vLaunchDEG: currAvg?.vLaunchDEG ?? null,
        hLaunchDEG: currAvg?.hLaunchDEG ?? null,
        maxDistanceFT: max(validCurrHits.map((h) => h.hit?.distanceFT)) ?? null,
        maxExitMPH: max(validCurrHits.map((h) => h.hit?.exitMPH)) ?? null,
      },
      previous: {
        exitMPH: prevAvg?.exitMPH ?? null,
        distanceFT: prevAvg?.distanceFT ?? null,
        vLaunchDEG: prevAvg?.vLaunchDEG ?? null,
        hLaunchDEG: prevAvg?.hLaunchDEG ?? null,
        maxDistanceFT: max(validPrevHits.map((h) => h.hit?.distanceFT)) ?? null,
        maxExitMPH: max(validPrevHits.map((h) => h.hit?.exitMPH)) ?? null,
      },
      deltas: {
        exitMPH: null,
        distanceFT: null,
        vLaunchDEG: null,
        hLaunchDEG: null,
        maxDistanceFT: null,
        maxExitMPH: null,
      },
    };

    output.deltas.exitMPH = getMetricDelta(
      output.current.exitMPH,
      output.previous.exitMPH
    );
    output.deltas.maxExitMPH = getMetricDelta(
      output.current.maxExitMPH,
      output.previous.maxExitMPH
    );

    output.deltas.distanceFT = getMetricDelta(
      output.current.distanceFT,
      output.previous.distanceFT
    );

    output.deltas.vLaunchDEG = getMetricDelta(
      output.current.vLaunchDEG,
      output.previous.vLaunchDEG
    );

    output.deltas.hLaunchDEG = getMetricDelta(
      output.current.hLaunchDEG,
      output.previous.hLaunchDEG
    );

    output.deltas.maxDistanceFT = getMetricDelta(
      output.current.maxDistanceFT,
      output.previous.maxDistanceFT
    );

    output.deltas.maxExitMPH = getMetricDelta(
      output.current.maxExitMPH,
      output.previous.maxExitMPH
    );

    return output;
  }, [currSummaryHits, prevSummaryHits, hitsFilter.period]);

  const { isMetric } = machine;

  const speedUnit = MetricHelper.getSpeedUnit(isMetric);
  const distanceUnit = MetricHelper.getDistanceUnit(isMetric);
  const speedMetricFn = isMetric ? MetricHelper.mphToKph : undefined;
  const distanceMetricFn = isMetric ? MetricHelper.ftToM : undefined;

  const avgLaunchAngle = formatHitStat(data.current.vLaunchDEG, 1, '°');
  const avgExitVel = formatHitStat(
    data.current.exitMPH,
    1,
    ` ${speedUnit}`,
    speedMetricFn
  );
  const maxExitVel = formatHitStat(
    data.current.maxExitMPH,
    1,
    ` ${speedUnit}`,
    speedMetricFn
  );
  const avgDist = formatHitStat(
    data.current.distanceFT,
    1,
    ` ${distanceUnit}`,
    distanceMetricFn
  );
  const maxDist = formatHitStat(
    data.current.maxDistanceFT,
    1,
    ` ${distanceUnit}`,
    distanceMetricFn
  );

  return (
    <ErrorBoundary identifier="HPSummaryKeyMetricAverages">
      <HPSummarySectionCard loading={loading}>
        <Flex height="100%" direction="column">
          <Flex align="center" gap="2">
            <Heading size="3">{t('hitters.key-metrics-averages')}</Heading>
            <CommonTooltip
              content={getTooltipDates(hitsFilter.period)}
              trigger={<InfoCircledIcon cursor="pointer" />}
            />
          </Flex>
          <Text color={RADIX.COLOR.NEUTRAL}>
            {t('hitters.showing-averages-bp-metrics')}
          </Text>

          <DataList.Root
            orientation="horizontal"
            style={{ gap: 0 }}
            mt="auto"
            mb="auto"
          >
            <DataRow
              label={t('hitters.avg-launch-angle')}
              value={avgLaunchAngle}
              delta={data.deltas.vLaunchDEG}
            />
            <Separator
              style={{ width: '100%', gridColumnStart: 'span 2' }}
              my="2"
            />
            <DataRow
              label={t('hitters.avg-exit-velocity')}
              value={avgExitVel}
              delta={data.deltas.exitMPH}
            />
            <Separator
              style={{ width: '100%', gridColumnStart: 'span 2' }}
              my="2"
            />
            <DataRow
              label={t('hitters.max-exit-velocity')}
              value={maxExitVel}
              delta={data.deltas.maxExitMPH}
            />
            <Separator
              style={{ width: '100%', gridColumnStart: 'span 2' }}
              my="2"
            />
            <DataRow
              label={t('hitters.avg-distance')}
              value={avgDist}
              delta={data.deltas.distanceFT}
            />
            <Separator
              style={{ width: '100%', gridColumnStart: 'span 2' }}
              my="2"
            />
            <DataRow
              label={t('hitters.max-distance')}
              value={maxDist}
              delta={data.deltas.maxDistanceFT}
            />
          </DataList.Root>
        </Flex>
      </HPSummarySectionCard>
    </ErrorBoundary>
  );
};
